import React, { useState } from "react";
import { useTheme } from "@emotion/react";
import { isMobile } from "react-device-detect";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Button, Grid, Tooltip, Divider } from "@mui/material";
import { GetAppOutlined } from "@mui/icons-material";
import { formatDate } from "../../../utils/date";
import { getFileTypeIcon } from "../../../utils/comment";
import { downloadFile, isImage } from "../../../utils/file";
import ConfirmPopup from "../../../component/popup/ConfirmPopup";

const CommentsList = ({ comment, compress, onCommentResolved, horizontal, user, onOpenCommentsDetails }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [isConfirming, setIsConfirming] = useState(false);
  const buttonStyle = {
    pr: 1.5,
    pl: 1,
    py: 0,
    lineHeight: "32px",
    borderRadius: "20px",
    my: 1,
  };

  const handleConfirmResolve = (event) => {
    event.stopPropagation();
    setIsConfirming(true);
  };

  const handleViewInModel = () => {
    navigate(`/modules/3DModel/${projectId}?selectedElementId=${comment.elementId}`);
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={horizontal ? 1 : 2}>
          <img
            src={`assets/icons/${comment.status === "Pending" ? "orangeclock" : "ok"}.svg`}
            alt={comment.status}
            style={{ verticalAlign: "bottom", marginRight: "4px" }}
          />
        </Grid>
        <Grid
          item
          xs={horizontal ? 5 : 10}
          sx={{ cursor: onOpenCommentsDetails ? "pointer" : "initial" }}
          onClick={() => {
            onOpenCommentsDetails && onOpenCommentsDetails(comment?.commentId);
          }}
        >
          <Typography>
            Comment ID: {comment.projectCommentId} - {comment.header}
          </Typography>
          <Typography>Category: {comment.commentType}</Typography>
          <Typography>{comment.body}</Typography>
          <Typography>
            By: {comment.createBy?.name}
            {" -> "}To: {comment.issueTo.name}
          </Typography>
          <Typography>Date Raised: {formatDate(comment.createDateTime)}</Typography>
          {!compress && horizontal && <Typography>Item Name: {comment.itemName}</Typography>}
          <Typography>Status: {comment.status}</Typography>
          {!compress && (
            <>
              {comment.commentType === "Safety Issue" && (
                <Typography>
                  Damaging Energies - <span>{comment.damagingEnergyValues.join(", ")}</span>
                </Typography>
              )}
              {comment.commentType === "Safety Issue" && (
                <Typography>
                  Positive Safety Observation - <span>{comment.positiveSafetyObservation ? "Yes" : "No"}</span>
                </Typography>
              )}
            </>
          )}
          {compress && (
            <Box display={"flex"} flexDirection={"column"} alignItems={"flex-start"}>
              {comment.commentFiles?.map((file, fileIndex) => (
                <Button
                  key={file.commentFileId}
                  size="small"
                  startIcon={<GetAppOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    downloadFile(file);
                  }}
                  sx={{
                    p: 0,
                    color: theme.palette.text.secondary,
                    fontSize: 12,
                    justifyContent: "flex-start",
                  }}
                >
                  <u>{file.filename}</u>
                </Button>
              ))}
            </Box>
          )}
          {!horizontal && !compress && (
            <Grid container>
              {comment.commentFiles.length > 0 && (
                <Grid item xs={12}>
                  <Divider sx={{ marginY: 2 }} />
                  <Typography>Attached files (click to download)</Typography>
                </Grid>
              )}
              {comment.commentFiles.map((file, index) => (
                <Grid item xs={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
                  <Button onClick={() => downloadFile(file)} sx={{ width: "100%", padding: 0 }}>
                    <Box sx={{ py: 0.5, textAlign: "center" }}>
                      <img
                        style={{ width: 47, height: 47 }}
                        alt={file.format}
                        src={isImage(file.format) ? `${file?.fileURL}` : getFileTypeIcon(file.format)}
                      />
                      <Tooltip title={file.filename + "." + file.format}>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#474747",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100px",
                          }}
                        >
                          {file.filename}.{file.format}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}
          {comment.status === "Resolved" ? (
            <>
              <Typography sx={{ mb: 2 }}>Resolved by: {comment.resolveBy?.name}</Typography>
            </>
          ) : (
            user?.userRole !== "Contractor" && (
              <Box textAlign={horizontal ? "left" : "center"}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<img src="assets/buttons/ok.svg" alt="Confirm Resolve" />}
                  sx={buttonStyle}
                  onClick={handleConfirmResolve}
                >
                  Confirm Resolve
                </Button>
              </Box>
            )
          )}
          {isMobile && comment.elementId && (
            <Box textAlign={horizontal ? "left" : "center"}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<img src="assets/icons/modelblue.svg" alt="View in Model" />}
                sx={buttonStyle}
                onClick={handleViewInModel}
              >
                View in Model
              </Button>
            </Box>
          )}
        </Grid>
        {horizontal && (
          <Grid item xs={6}>
            <Grid container>
              {comment.commentFiles.length > 0 && (
                <Grid item xs={12}>
                  <Typography>Attached files (click to download)</Typography>
                </Grid>
              )}
              {comment.commentFiles.map((file, index) => (
                <Grid item xs={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
                  <Button onClick={() => downloadFile(file)} sx={{ width: "100%", padding: 0 }}>
                    <Box sx={{ py: 0.5, textAlign: "center" }}>
                      <img
                        style={{ width: 47, height: 47 }}
                        alt={file.format}
                        src={isImage(file.format) ? `${file?.fileURL}` : getFileTypeIcon(file.format)}
                      />
                      <Tooltip title={file.filename + "." + file.format}>
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#474747",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "120px",
                          }}
                        >
                          {file.filename}.{file.format}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
      <ConfirmPopup
        open={isConfirming}
        message="Confirm to resolve this comment?"
        onCancel={() => setIsConfirming(false)}
        onConfirm={() => {
          onCommentResolved(comment.commentId);
          setIsConfirming(false);
        }}
      ></ConfirmPopup>
    </>
  );
};

export default CommentsList;
